/* eslint max-lines-per-function: ["error", {max: 379}] -- this is a complex component */
/* eslint max-statements: ["error", {max: 36}] -- this is a complex component */
/* eslint max-lines: ["error", {max: 467}] -- this is a complex component */
import type { PropsWithChildren, RefObject } from "react";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { differenceInMilliseconds } from "date-fns";
import isNil from "lodash/isNil";
import {
  AppState,
  Keyboard,
  NativeEventEmitter,
  NativeModules,
  View,
} from "react-native";

import {
  AnalyticsFieldName,
  RootAnalyticsEvent,
} from "@/constants/analytics/Analytics";
import { LogoutReason } from "@/constants/Login";
import { IncomingWebViewEvent } from "@/constants/WebView";
import type { StateChange } from "@/hooks/useAppForegroundState";
import useAppForegroundState from "@/hooks/useAppForegroundState";
import useAuth from "@/hooks/useAuth";
import useHelpCenter from "@/hooks/useHelpCenter";
import { ModalLockSources, useModalTracker } from "@/hooks/useModalTracker";
import { useAnalytics } from "@/services/Analytics";
import { captureMessage } from "@/services/AppMonitor";
import {
  DefaultUserInactivityTimeoutMs,
  isBackgrounding,
  isForegrounding,
} from "@/utils/appInactivity.util";
import { toMs } from "@/utils/date.util";
import { isWeb } from "@/utils/platform.util";
import { createThrottledCallback } from "@/utils/throttle.util";
import { isValidWebOrigin } from "@/utils/webView.util";

type UserActivityContextValue = {
  foregroundGeneration: number;
  isInactive: boolean;
  markActivity: () => void;
  setBackgroundInactivityHandler: (
    handlerRef: RefObject<() => Promise<void>>,
  ) => void;
};

type AppStateSnapshot = {
  state: string;
  when: string;
};

const snapshot = (state: string): AppStateSnapshot => ({
  state,
  when: new Date().toISOString(),
});

// Web DOM events to listen for user activity (extracted to avoid recreating on every render)
const WebActivityEvents = [
  "mousemove",
  "wheel",
  "keydown",
  "keyup",
  "input",
  "change",
  "touchstart",
  "touchmove",
  "click",
  "focus",
  "scroll",
  "pointerdown",
  "pointermove",
] as const;

const UserActivityContext = createContext<undefined | UserActivityContextValue>(
  undefined,
);

const UserActivityProvider = ({ children }: PropsWithChildren) => {
  const { isAuthenticated, isLoading, logout } = useAuth();
  // Skip API calls on cold start until Auth0 credential validation completes (prevents stale cached state from triggering premature API calls)
  const [isInitializingOnColdStart, setIsInitializingOnColdStart] =
    useState<boolean>(true);

  // while boolean useState values may feel more intuitive here for isProcessing/isForegrounding,
  // React 19+ aggressively auto-batches, meaning false/true/false transitions become no-ops and
  // never trigger downstream useEffects. to ensure intermediate state transitions are propagated,
  // this must instead be a monotonically increasing counter (that React *cannot* auto-batch in any way)
  const [foregroundGeneration, setForegroundGeneration] = useState<number>(0);
  const [isInactive, setIsInactive] = useState<boolean>(false);
  const [wentInactiveDate, setWentInactiveDate] = useState<Date>(new Date());
  const { hideInAppNotifications } = useHelpCenter({
    shouldSkipGetFullProfile: isInitializingOnColdStart,
  });

  useEffect(() => {
    if (isLoading || isAuthenticated) return;
    setIsInitializingOnColdStart(false);
  }, [isLoading, isAuthenticated]);

  const appStateHistoryRef = useRef<AppStateSnapshot[]>([
    snapshot(AppState.currentState),
  ]);

  const { track } = useAnalytics();
  const { preventNativeModals } = useModalTracker();
  const userInactivityTimeoutIdRef = useRef<NodeJS.Timeout | null>(null);
  const backgroundInactivityHandlerRef = useRef<null | RefObject<
    () => Promise<void>
  >>(null);
  const nativeEventEmitterRef = useRef<NativeEventEmitter | null>(null);
  const nativeSubscriptionRef = useRef<null | ReturnType<
    NativeEventEmitter["addListener"]
  >>(null);
  const isInactivityHandlerRunningRef = useRef<boolean>(false);

  const clearUserInactivityTimer = useCallback(() => {
    if (!userInactivityTimeoutIdRef.current) return;
    clearTimeout(userInactivityTimeoutIdRef.current);
    userInactivityTimeoutIdRef.current = null;
  }, []);

  const invokeInactivityCallback = useCallback(async () => {
    const callback = backgroundInactivityHandlerRef.current?.current;
    if (!callback) {
      // Fallback: if callback not registered yet, route directly to login
      await logout(LogoutReason.InactivityTimeout);
      return;
    }

    try {
      await callback();
    } catch (error) {
      // If callback throws, capture error and fallback to login
      captureMessage("User inactivity callback threw error", {
        context: { error },
      });
      await logout(LogoutReason.InactivityTimeout);
    }
  }, [logout]);

  const handleUserInactivity = useCallback(async () => {
    // Guard: prevent double invocation if both foreground and background handlers trigger
    if (isInactivityHandlerRunningRef.current) return;
    isInactivityHandlerRunningRef.current = true;

    try {
      const appStateHistory = [...appStateHistoryRef.current];
      const currentAppState = appStateHistory[0]?.state;
      if (currentAppState !== "active") {
        captureMessage(
          "User inactivity timeout unexpectedly invoked when app is not 'active'",
          {
            context: {
              appStateHistory,
              currentAppState,
              historyLength: appStateHistory.length,
            },
          },
        );
      }

      setIsInactive(true);
      if (!isAuthenticated) return;

      // Hide any open native modals
      await hideInAppNotifications();
      preventNativeModals(ModalLockSources.InactivityTimeout);

      // Invoke the registered callback to trigger biometric authentication
      await invokeInactivityCallback();
    } finally {
      isInactivityHandlerRunningRef.current = false;
    }
  }, [
    isAuthenticated,
    preventNativeModals,
    hideInAppNotifications,
    invokeInactivityCallback,
  ]);

  const startUserInactivityTimer = useCallback(() => {
    clearUserInactivityTimer();
    userInactivityTimeoutIdRef.current = setTimeout(
      () => void handleUserInactivity(),
      DefaultUserInactivityTimeoutMs,
    );
  }, [clearUserInactivityTimer, handleUserInactivity]);

  const markActivity = useCallback(() => {
    // only count activity when the app/tab is actually in the foreground.
    // while the appState handler below should disable the timer when backgrounded,
    // this is added as an extra layer of safety to be sure.
    if (isWeb() && AppState.currentState !== "active") return false;
    if (isInactive) setIsInactive(false);
    startUserInactivityTimer();
    return true;
  }, [isInactive, startUserInactivityTimer]);

  const setBackgroundInactivityHandler = useCallback(
    (handlerRef: RefObject<() => Promise<void>>) => {
      backgroundInactivityHandlerRef.current = handlerRef;
    },
    [],
  );

  // Background inactivity logic
  const handleForegroundStateChange = useCallback(
    ({ currentState, nextState }: StateChange) => {
      const now = new Date();
      const appStateHistory = [...appStateHistoryRef.current];
      appStateHistoryRef.current.unshift(snapshot(nextState));

      // If the app was active and is now inactive, record the time
      if (isBackgrounding({ currentState, nextState })) {
        track(RootAnalyticsEvent.Backgrounded);
        setWentInactiveDate(now);
        // Pause user inactivity timer while in background
        clearUserInactivityTimer();
        return;
      }

      // If the app is not foregrounding, do nothing
      if (!isForegrounding({ currentState, nextState })) {
        const wasEverActive = appStateHistory
          .map((snap) => snap.state)
          .includes("active");
        if (!wasEverActive) {
          captureMessage(
            "User app state is somehow backgrounded but was never active",
            {
              context: {
                appStateHistory,
                currentState,
                historyLength: appStateHistory.length,
                nextState,
              },
            },
          );
        }
        return;
      }

      let isCallbackRunning = false;
      try {
        const inactivityDurationMs = differenceInMilliseconds(
          now,
          wentInactiveDate,
        );

        // If the app was inactive for longer than the inactivity limit
        const isInactivityTimeout =
          inactivityDurationMs >= DefaultUserInactivityTimeoutMs;
        track(RootAnalyticsEvent.Foregrounded, {
          [AnalyticsFieldName.InactivityDurationMs]: inactivityDurationMs,
          [AnalyticsFieldName.InactivityLimitMs]:
            DefaultUserInactivityTimeoutMs,
          [AnalyticsFieldName.IsInactivityTimeout]: isInactivityTimeout,
        });

        if (!isInactivityTimeout) {
          // Resume user inactivity timer on foreground
          markActivity();
          return;
        }

        const callback = backgroundInactivityHandlerRef.current?.current;
        if (isNil(callback)) return;

        // Invoke the registered callback if timeout occurred
        isCallbackRunning = true;
        // the callback is not awaited to not block further processing of this Provider
        void callback().finally(() => {
          setForegroundGeneration((prev) => prev + 1);
        });
      } finally {
        // if callback is running, it will increment the counter when done
        if (isCallbackRunning) return;
        setForegroundGeneration((prev) => prev + 1);
      }
    },
    [track, wentInactiveDate, clearUserInactivityTimer, markActivity],
  );

  const onChangeRef = useRef(handleForegroundStateChange);
  useEffect(() => {
    onChangeRef.current = handleForegroundStateChange;
  }, [handleForegroundStateChange]);

  useAppForegroundState({
    onChangeRef,
  });

  const markActivityRef = useRef(markActivity);
  useEffect(() => {
    markActivityRef.current = markActivity;
  }, [markActivity]);

  // Native module setup for user activity detection
  // - Broader activity coverage: Some interactions don’t reach React’s responder system (e.g., certain scrolls with native driver, text input quirks, video/map components, WebView).
  // - Reliability across the app: Native integrates below your React tree, so you don’t need every component to cooperate for activity to be detected.
  // - Lower friction: Instead of wiring many JS listeners, native can centrally signal “user did something” no matter which screen/component handled it.

  useEffect(() => {
    let cancelled = false;
    const setupNative = async () => {
      try {
        const { UserActivityDetection } = NativeModules;
        if (!UserActivityDetection) {
          return;
        }
        let isAvailable: boolean;
        if (typeof UserActivityDetection.isAvailable === "function") {
          isAvailable = Boolean(await UserActivityDetection.isAvailable());
        } else {
          isAvailable = Boolean(UserActivityDetection.isAvailable);
        }
        if (cancelled || !isAvailable) {
          return;
        }

        nativeEventEmitterRef.current = new NativeEventEmitter(
          UserActivityDetection,
        );
        nativeSubscriptionRef.current =
          nativeEventEmitterRef.current.addListener(
            "UserActivityDetected",
            () => {
              markActivity();
            },
          );
      } catch {
        // Native module not available, continue without it
      }
    };
    void setupNative();
    return () => {
      cancelled = true;
      nativeSubscriptionRef.current?.remove();
      nativeSubscriptionRef.current = null;
      nativeEventEmitterRef.current = null;
    };
  }, [markActivity]);

  // Keyboard listener (activity signal)
  useEffect(() => {
    const hide = Keyboard.addListener("keyboardDidHide", markActivity);
    const show = Keyboard.addListener("keyboardDidShow", markActivity);
    return () => {
      hide.remove();
      show.remove();
    };
  }, [markActivity]);

  // Web-specific DOM event listeners (activity signal)
  useEffect(() => {
    if (!isWeb()) return;

    const handleDOMActivity = createThrottledCallback({
      callback: markActivityRef.current,
      throttleMs: toMs({ seconds: 10 }),
    });

    // eslint-disable-next-line @typescript-eslint/naming-convention -- passive is a DOM API property
    const options: AddEventListenerOptions = { passive: true };

    WebActivityEvents.forEach((event) => {
      document.addEventListener(event, handleDOMActivity, options);
    });

    // Detect mouse entering/leaving iframe boundaries (for Intercom, assessments, etc.)
    // Note: Cannot detect events INSIDE cross-origin iframes due to browser security
    const iframes = document.querySelectorAll("iframe");
    iframes.forEach((iframe) => {
      iframe.addEventListener("mouseenter", handleDOMActivity);
      iframe.addEventListener("mouseleave", handleDOMActivity);
    });

    // Listen for messages from assessment iframe (marks activity when user edits survey fields)
    const handleWebViewMessage = (event: MessageEvent) => {
      if (!isValidWebOrigin(event.origin)) return;

      try {
        const parsed = JSON.parse(event.data);
        if (parsed?.type === IncomingWebViewEvent.AssessmentProgressSaving) {
          markActivityRef.current();
        }
      } catch {
        // Ignore non-JSON messages
      }
    };

    window.addEventListener("message", handleWebViewMessage);

    return () => {
      WebActivityEvents.forEach((event) => {
        document.removeEventListener(event, handleDOMActivity, options);
      });
      iframes.forEach((iframe) => {
        iframe.removeEventListener("mouseenter", handleDOMActivity);
        iframe.removeEventListener("mouseleave", handleDOMActivity);
      });
      window.removeEventListener("message", handleWebViewMessage);
    };
  }, []);

  // Initializing timer on mount
  useEffect(() => {
    startUserInactivityTimer();
    return () => clearUserInactivityTimer();
  }, [startUserInactivityTimer, clearUserInactivityTimer]);

  // Touch handlers: capture touches to mark activity
  const handleMoveShouldSetResponderCapture = useCallback(() => {
    markActivity();
    return false;
  }, [markActivity]);

  const handleResponderTerminationRequest = useCallback(() => {
    markActivity();
    return false;
  }, [markActivity]);

  const handleStartShouldSetResponderCapture = useCallback(() => {
    markActivity();
    return false;
  }, [markActivity]);

  const value: UserActivityContextValue = useMemo(
    () => ({
      foregroundGeneration,
      isInactive,
      markActivity,
      setBackgroundInactivityHandler,
    }),
    [
      isInactive,
      foregroundGeneration,
      markActivity,
      setBackgroundInactivityHandler,
    ],
  );

  return (
    <UserActivityContext.Provider value={value}>
      <View
        collapsable={false}
        onMoveShouldSetResponderCapture={handleMoveShouldSetResponderCapture}
        onResponderTerminationRequest={handleResponderTerminationRequest}
        onStartShouldSetResponderCapture={handleStartShouldSetResponderCapture}
        style={{ flex: 1 }}
      >
        {children}
      </View>
    </UserActivityContext.Provider>
  );
};

export { UserActivityContext, UserActivityProvider, WebActivityEvents };
